import { FileText, Info } from "lucide-react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { FileUpload } from "../FileUpload";
import { Section, Field } from "./builderUi";
import { PayoutInline } from "./PayoutInline";

export const CATEGORIES = [
  "Trip home", "Study abroad", "Move-in essentials", "Textbooks & tools", "Laptop / Tech",
  "Housing", "Transportation", "Emergency", "First Gen", "Other",
];

const DOC_THRESHOLD = 500;

export const FundingNeedSection = ({ form, set, campaignId, goalCap, durationDays, requestDocId, onDocChange }) => {
  const goalNum = Number(form.goal) || 0;
  const exceedsCap = goalCap != null && goalNum > goalCap;
  const requiresDoc = goalNum > DOC_THRESHOLD;
  const docRecord = requestDocId
    ? { id: requestDocId, url: `/api/files/${requestDocId}?auth=${localStorage.getItem("mra_jwt") || ""}`, original_filename: "Supporting document", content_type: "document" }
    : null;
  const categories = form.category && !CATEGORIES.includes(form.category) ? [form.category, ...CATEGORIES] : CATEGORIES;

  return (
    <Section accent="bg-yellow" title="Funding need" subtitle="Tell us what you need, how much, and where grants should land." testid="builder-funding">
      <div data-testid="policy-summary-strip" className="mb-6 flex flex-wrap items-center gap-4 rounded-md border border-tangerine/25 bg-tangerine/[0.05] px-4 py-3 text-[13px] text-ink">
        <div className="flex items-center gap-1.5">
          <Info className="h-3.5 w-3.5 text-tangerine" strokeWidth={2} />
          <span className="font-semibold uppercase tracking-[0.16em] text-tangerine">Beta launch</span>
        </div>
        <span>Grant cap: <strong className="text-ink">{goalCap != null ? `$${goalCap}` : "TBD"}</strong></span>
        <span>Campaign length: <strong className="text-ink">{durationDays} days</strong></span>
        <span>Disbursement: <strong className="text-ink">weekly, equal installments</strong></span>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="md:col-span-2">
          <Field label="Campaign title" hint="The first line donors read. Say what the money does.">
            <Input data-testid="builder-title" value={form.title} onChange={set("title")} placeholder="Help me get to Kyoto for study abroad" maxLength={140} className="font-serif text-3xl" />
          </Field>
        </div>
        <Field
          label="Goal amount (USD)"
          hint={exceedsCap ? `Above the current cap ($${goalCap}). Please lower to submit.` : goalCap != null ? `Maximum during beta: $${goalCap}.` : undefined}
        >
          <Input
            data-testid="builder-goal"
            type="number"
            min={1}
            max={goalCap ?? undefined}
            value={form.goal}
            onChange={set("goal")}
            placeholder={goalCap != null ? String(goalCap) : "500"}
            className={exceedsCap ? "border-scarlet ring-scarlet/30" : ""}
          />
        </Field>
        <Field label="Type of need">
          <Select value={form.category} onValueChange={(v) => set("category")(v)}>
            <SelectTrigger data-testid="builder-category"><SelectValue placeholder="Choose a category" /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Deadline" optional hint="If the money is needed by a date — a deposit, a flight, a move-in.">
          <Input data-testid="builder-deadline" type="date" value={form.deadline} onChange={set("deadline")} />
        </Field>
      </div>
      <div className="mt-5">
        <Field label="Funding need description" counter={`${form.story.length}/1000`}>
          <Textarea data-testid="builder-story" maxLength={1000} rows={6} value={form.story} onChange={set("story")} placeholder="Write the way you'd tell a friend — what the money is for, what it changes, why it matters right now." />
        </Field>
      </div>

      {requiresDoc && (
        <div data-testid="docs-upload-zone" className="mt-6 rounded-md border border-cobalt/30 bg-cobalt/[0.04] p-5">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-cobalt" strokeWidth={1.75} />
            <span className="text-[13px] font-semibold uppercase tracking-[0.2em] text-cobalt">Supporting documentation required</span>
          </div>
          <p className="mt-2 text-base leading-relaxed text-ink">
            Campaigns requesting more than <strong>${DOC_THRESHOLD}</strong> need paperwork behind the ask — a receipt, a quote, a deposit letter, an itemized budget.
          </p>
          <FileUpload
            purpose="student_doc"
            label="Supporting document (admin only)"
            hint="PDF or image, up to 15 MB. Donors never see it."
            testid="builder-upload-doc"
            campaignId={campaignId}
            value={docRecord}
            onChange={onDocChange}
            className="mt-4"
          />
        </div>
      )}

      <PayoutInline />
    </Section>
  );
};
